/* eslint-disable complexity */
import { RoomWebsocketErrorCode, RoomErrorCode } from '@tencent/docs-connection';
import {
    REASON_FOR_CONNECT_MODE,
    REASON_FOR_FATAL_ERROR,
    REASON_FOR_KICK_OUT,
    REASON_FOR_NEED_FETCH,
    REASON_FOR_NO_PRIVILEGE,
    REASON_FOR_RETRY,
    REASON_FOR_ROOM,
    REASON_FOR_SIZE_TOO_LARGE,
    RetrySource,
    ServerErrorCode,
    SlideAppError,
} from '../constants';
import { getRetryIntervalByRetryCount } from '../utils';
import { type IRevisionHandler } from '../types';
import type { ErrorSource } from '../constants';
import type { IRoomSendMessageResult } from '@tencent/docs-connection';

export abstract class SubmitHandler {
    protected abstract errorSource: ErrorSource;

    constructor(protected submitHandler: IRevisionHandler, protected moduleName: string) {}

    protected get retryCommitCount() {
        return this.submitHandler.getRetrySubmitCount();
    }
    protected handleResponseError(taskId: number, result: IRoomSendMessageResult) {
        const errorCode = this.getErrorCode(result);
        const { msg = '' } = result?.response?.body?.data || {};
        if (REASON_FOR_ROOM.includes(errorCode)) {
            // 处理房间层错误码
            return this.handleRoomErrorCode(taskId, errorCode, msg);
        }
        if (REASON_FOR_NEED_FETCH.includes(errorCode)) {
            // 基础版本号错误，拉断层。3.0的错误信息中，不再包含最新版本号
            return this.submitHandler.handleBaseVersionTooLow(taskId, errorCode);
        }
        if (
            [
                ...REASON_FOR_FATAL_ERROR,
                ...REASON_FOR_SIZE_TOO_LARGE,
                ...REASON_FOR_NO_PRIVILEGE,
                ...REASON_FOR_KICK_OUT,
            ].includes(errorCode)
        ) {
            // 严重错误，不再重试
            return this.submitHandler.handleFatalError(taskId, errorCode, msg, this.errorSource);
        }
        if ([...REASON_FOR_RETRY, REASON_FOR_CONNECT_MODE].includes(errorCode)) {
            // 重试状态码
            return this.handleCommitErrorForRetry(taskId, result, errorCode, msg);
        }
        return this.handleCommitErrorForRetry(taskId, result, errorCode, msg);
    }

    protected getErrorCode(result: IRoomSendMessageResult): number {
        // 后台连接层、房间层的错误码
        const errCode = result.response?.onpost?.result;
        // 后台编辑层的错误码
        const errorCode = result.response?.body?.data.reason || result.errCode;
        return errCode || errorCode;
    }

    protected handleConnectionLayerResponse(result: IRoomSendMessageResult) {
        let errorCode = 0;
        if (result.errCode !== 0 || !result.response) {
            errorCode = this.getErrorCode(result);
        }
        return errorCode;
    }

    protected handleCommitErrorForRetry(taskId: number, result: IRoomSendMessageResult, errCode: number, msg: string) {
        // 重复提交无需重试
        if (msg.includes(`${ServerErrorCode.COMMIT_DUPLICATE}`)) {
            return this.submitHandler.handleDuplicateSubmit(taskId, errCode, this.errorSource);
        }
        if (errCode === RoomWebsocketErrorCode.SOCKET_SWITCH_BIG_COLLAB) {
            this.submitHandler.switchBigCollabMode();
        }
        if (this.retryCommitCount < 3) {
            this.submitHandler.updateRetrySubmitCount(this.retryCommitCount + 1);
            this.submitHandler.logger.info(
                this.moduleName,
                `retry commit for commit error, limit (${this.retryCommitCount} / 3), errCode:${errCode}`,
            );
            const nextRetryInterval: number =
                result.response.body?.data?.retry_interval ?? getRetryIntervalByRetryCount(this.retryCommitCount);
            this.submitHandler.retrySubmit(RetrySource.CommitError, nextRetryInterval);
            return;
        }
        this.submitHandler.logger.error(this.moduleName, 'commit failed for reaching retry limit ', errCode);
        this.submitHandler.handleFatalError(taskId, errCode, msg ?? 'commit retry exceed limit', this.errorSource);
    }

    /**
     * 处理前端网络层的错误码
     * @param taskId
     * @param cmd
     * @param result
     */
    protected handleRoomErrorCode = (taskId: number, errCode: number, message: string) => {
        this.submitHandler.logger.warn(this.moduleName, `handle room error: ${errCode}`);
        switch (errCode) {
            case RoomErrorCode.EXIST:
            // 之前106补发的原因是离线第一次打开，发送数据会早于房间层成功建立连接。这次修改在发送的地方做了判断，建立成功后再发送。
            case RoomErrorCode.ROOM_STATE_ERR:
                break;
            case RoomErrorCode.TIMEOUT:
            case RoomErrorCode.ROOM_DISCONNECT:
            // 按道理来说105不该重新发送的，但目前离线/(62000/60006/60007)都会造成105，后者会从房间层的发送队列中移除，需要业务侧手动补发
            case RoomErrorCode.ROOM_CLOSE:
                this.submitHandler.retrySubmit(RetrySource.RoomError, 100000);
                break;
            case RoomErrorCode.PARA_ERROR:
            case RoomErrorCode.ROOM_SHUTDOWN:
                this.submitHandler.dispatchError({
                    error: SlideAppError.Timeout,
                    code: errCode,
                    taskId,
                    message: message ?? '房间已关闭',
                    shutDown: true,
                    source: this.errorSource,
                });
                break;
            default:
                this.submitHandler.dispatchError({
                    error: SlideAppError.Timeout,
                    code: errCode,
                    taskId,
                    message: message ?? 'unknown',
                    source: this.errorSource,
                });
                break;
        }
    };
}
